import type {
  ClonePdfTemplateRequest,
  CreatePdfSectionInput,
  UpdatePdfSectionInput,
  UpdatePdfTemplateAttachmentInput,
  UpdatePdfTemplateStyleInput,
} from "@repo/shared/types";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as pdfApi from "@/lib/api/pdf-templates";
import { CLONE_JOBS_KEY } from "@/lib/hooks/use-clone-jobs";
import type {
  AddPdfComponentInput,
  ReorderColumnComponentsInput,
  UpdatePdfComponentInput,
  UpdatePdfComponentOrderInput,
  UpdatePdfLayoutInput,
  UpdatePdfTemplateSettingsInput,
} from "@/lib/types/pdf-template";

// Query key factory
const keys = {
  all: ["pdf-templates"] as const,
  detail: (id: string | null) => ["pdf-templates", id] as const,
  page: (id: string | null) => ["pdf-templates", id, "page"] as const,
  sectionPage: (id: string | null) => ["pdf-templates", "sections", id, "page"] as const,
  sectionPages: (id: string | null) => ["pdf-templates", "sections", id, "pages"] as const,
  sections: (id: string | null) => ["pdf-templates", id, "sections"] as const,
  flowFields: (id: string | null) => ["pdf-templates", id, "flow-fields"] as const,
  completedSessions: (id: string | null) => ["pdf-templates", id, "completed-sessions"] as const,
  byFlow: (flowId: string | null) => ["pdf-templates", "by-flow", flowId] as const,
  attachments: (id: string | null) => ["pdf-templates", id, "attachments"] as const,
};

// Scoped invalidation helpers. Mutations previously invalidated the whole
// ["pdf-templates"] prefix, causing 5+ refetches per edit and tripping the
// 100 req/60s rate limit during active template editing (429s).
// Component/section mutations only receive child ids, so page/section list
// queries are matched by key shape instead of template id.

/** Refetch every PDF template query, for a template written outside this tab's mutations (a background copy). */
export const invalidatePdfTemplates = (qc: QueryClient) =>
  qc.invalidateQueries({ queryKey: keys.all });

/** Page-content queries: ["pdf-templates", id, "page"], ["pdf-templates", "sections", id, "page"|"pages"]. */

function invalidatePages(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "pdf-templates" &&
      (q.queryKey[q.queryKey.length - 1] === "page" ||
        q.queryKey[q.queryKey.length - 1] === "pages"),
  });
}

/** Section-list queries: ["pdf-templates", id, "sections"]. */
function invalidateSectionLists(qc: QueryClient) {
  void qc.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "pdf-templates" && q.queryKey[q.queryKey.length - 1] === "sections",
  });
}

/** Template list + detail (exact — detail key is a prefix of child keys). */
function invalidateTemplate(qc: QueryClient, id: string) {
  void qc.invalidateQueries({ queryKey: keys.all, exact: true });
  void qc.invalidateQueries({ queryKey: keys.detail(id), exact: true });
}

// --- Query hooks ---

export function usePdfTemplates() {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.all,
    queryFn: pdfApi.list,
  });
  return { templates: data ?? [], error, isLoading };
}

export function usePdfTemplate(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.detail(id),
    queryFn: () => pdfApi.getById(id!),
    enabled: !!id,
  });
  return { template: data ?? null, error, isLoading };
}

export function usePdfSectionPage(sectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.sectionPage(sectionId),
    queryFn: () => pdfApi.getSectionPage(sectionId!),
    enabled: !!sectionId,
  });
  return { page: data ?? null, error, isLoading };
}

// QP-208 — all pages of a section, ordered (multi-page sections from code templates)
export function usePdfSectionPages(sectionId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.sectionPages(sectionId),
    queryFn: () => pdfApi.getSectionPages(sectionId!),
    enabled: !!sectionId,
  });
  return { pages: data ?? [], error, isLoading };
}

export function usePdfSections(templateId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.sections(templateId),
    queryFn: () => pdfApi.getSections(templateId!),
    enabled: !!templateId,
  });
  return { sections: data ?? [], error, isLoading };
}

export function usePdfFlowFields(templateId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.flowFields(templateId),
    queryFn: () => pdfApi.getFlowFields(templateId!),
    enabled: !!templateId,
  });
  return { fields: data ?? [], error, isLoading };
}

export function usePdfCompletedSessions(templateId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.completedSessions(templateId),
    queryFn: () => pdfApi.getCompletedSessions(templateId!),
    enabled: !!templateId,
  });
  return { sessions: data ?? [], error, isLoading };
}

export function usePdfTemplatesByFlow(flowId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.byFlow(flowId),
    queryFn: () => pdfApi.getByFlow(flowId!),
    enabled: !!flowId,
  });
  return { templates: data ?? [], error, isLoading };
}

// --- Template mutation hooks ---

export function useCreatePdfTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.create,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all, exact: true });
      void qc.invalidateQueries({ queryKey: ["pdf-templates", "by-flow"] });
    },
  });
}

export function useClonePdfTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & ClonePdfTemplateRequest) =>
      pdfApi.clone(id, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all, exact: true });
      void qc.invalidateQueries({ queryKey: ["pdf-templates", "by-flow"] });
      // QL-334 — a translated copy is queued: start polling its progress.
      void qc.invalidateQueries({ queryKey: CLONE_JOBS_KEY });
    },
  });
}

export function useCreatePdfTemplateFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.createFromTemplate,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all, exact: true });
      void qc.invalidateQueries({ queryKey: ["pdf-templates", "by-flow"] });
    },
  });
}

export function useUpdatePdfTemplateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePdfTemplateSettingsInput }) =>
      pdfApi.updateSettings(id, data),
    onSuccess: (_data, { id }) => {
      invalidateTemplate(qc, id);
      void qc.invalidateQueries({ queryKey: ["pdf-templates", "by-flow"] });
    },
  });
}

export function useUpdatePdfTemplateStyle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePdfTemplateStyleInput }) =>
      pdfApi.updateStyle(id, data),
    onSuccess: (_data, { id }) => invalidateTemplate(qc, id),
  });
}

export function useDeletePdfTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.remove,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.all }),
  });
}

// --- Section mutation hooks ---

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: CreatePdfSectionInput }) =>
      pdfApi.createSection(templateId, data),
    onSuccess: (_data, { templateId }) =>
      void qc.invalidateQueries({ queryKey: keys.sections(templateId) }),
  });
}

export function useAddPortfolioSubNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ goalSectionId }: { goalSectionId: string }) =>
      pdfApi.createPortfolioSubNode(goalSectionId),
    onSuccess: () => {
      invalidateSectionLists(qc);
      invalidatePages(qc);
    },
  });
}

export function useUpdateSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, data }: { sectionId: string; data: UpdatePdfSectionInput }) =>
      pdfApi.updateSection(sectionId, data),
    onSuccess: () => {
      invalidateSectionLists(qc);
      invalidatePages(qc);
    },
  });
}

export function useDeleteSection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.deleteSection,
    onSuccess: () => {
      invalidateSectionLists(qc);
      invalidatePages(qc);
    },
  });
}

export function useReorderSections() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: { sectionIds: string[] } }) =>
      pdfApi.reorderSections(templateId, data),
    onSuccess: (_data, { templateId }) =>
      void qc.invalidateQueries({ queryKey: keys.sections(templateId) }),
  });
}

// --- Attachment hooks (QP-220) ---

export function usePdfAttachments(templateId: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: keys.attachments(templateId),
    queryFn: () => pdfApi.getAttachments(templateId!),
    enabled: !!templateId,
  });
  return { attachments: data ?? [], error, isLoading };
}

export function useUploadPdfAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, file }: { templateId: string; file: File }) =>
      pdfApi.uploadAttachment(templateId, file),
    onSuccess: (_data, { templateId }) =>
      void qc.invalidateQueries({ queryKey: keys.attachments(templateId) }),
  });
}

export function useUpdatePdfAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      attachmentId,
      data,
    }: {
      templateId: string;
      attachmentId: string;
      data: UpdatePdfTemplateAttachmentInput;
    }) => pdfApi.updateAttachment(attachmentId, data),
    onSuccess: (_data, { templateId }) =>
      void qc.invalidateQueries({ queryKey: keys.attachments(templateId) }),
  });
}

export function useDeletePdfAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ attachmentId }: { templateId: string; attachmentId: string }) =>
      pdfApi.deleteAttachment(attachmentId),
    onSuccess: (_data, { templateId }) =>
      void qc.invalidateQueries({ queryKey: keys.attachments(templateId) }),
  });
}

export function useReorderPdfAttachments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, attachmentIds }: { templateId: string; attachmentIds: string[] }) =>
      pdfApi.reorderAttachments(templateId, { attachmentIds }),
    onSuccess: (_data, { templateId }) =>
      void qc.invalidateQueries({ queryKey: keys.attachments(templateId) }),
  });
}

// --- Component mutation hooks ---

export function useAddComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pageId, data }: { pageId: string; data: AddPdfComponentInput }) =>
      pdfApi.addComponent(pageId, data),
    onSuccess: () => {
      invalidatePages(qc);
      invalidateSectionLists(qc);
    },
  });
}

export function useUpdateComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ componentId, data }: { componentId: string; data: UpdatePdfComponentInput }) =>
      pdfApi.updateComponent(componentId, data),
    onSuccess: () => invalidatePages(qc),
  });
}

export function useUpdateComponentOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      componentId,
      data,
    }: {
      componentId: string;
      data: UpdatePdfComponentOrderInput;
    }) => pdfApi.updateComponentOrder(componentId, data),
    onSuccess: () => invalidatePages(qc),
  });
}

export function useDeleteComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.deleteComponent,
    onSuccess: () => {
      invalidatePages(qc);
      invalidateSectionLists(qc);
    },
  });
}

export function useDuplicateComponent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.duplicateComponent,
    onSuccess: () => {
      invalidatePages(qc);
      invalidateSectionLists(qc);
    },
  });
}

export function useReorderItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pageId,
      data,
    }: {
      pageId: string;
      data: { pageItems: Array<{ id: string; type: string; order: number }> };
    }) => pdfApi.reorderItems(pageId, data),
    onSuccess: () => invalidatePages(qc),
  });
}

export function useMoveComponentToStandalone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ componentId, data }: { componentId: string; data: { targetIndex: number } }) =>
      pdfApi.moveComponentToStandalone(componentId, data),
    onSuccess: () => invalidatePages(qc),
  });
}

// --- Layout mutation hooks ---

export function useCreateLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.createLayout,
    onSuccess: () => invalidatePages(qc),
  });
}

export function useDeleteLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: pdfApi.deleteLayout,
    onSuccess: () => invalidatePages(qc),
  });
}

export function useUpdateLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ layoutId, data }: { layoutId: string; data: UpdatePdfLayoutInput }) =>
      pdfApi.updateLayout(layoutId, data),
    onSuccess: () => invalidatePages(qc),
  });
}

export function useReorderColumnComponents() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      layoutId,
      column,
      data,
    }: {
      layoutId: string;
      column: string;
      data: ReorderColumnComponentsInput;
    }) => pdfApi.reorderColumnComponents(layoutId, column, data),
    onSuccess: () => invalidatePages(qc),
  });
}

// --- Preview mutation hook ---

export function useGeneratePreview() {
  return useMutation({
    mutationFn: pdfApi.generatePreview,
  });
}
